import type { Certification } from 'src/types/Certification';
import type { CertStatusDetail } from 'src/types/CertStatus';
import { DECISION_PHASES } from 'src/types/CertStatus';

/**
 * Returns the survey ID for the given cert member in this certification.
 * Accepts either a cert_member_id number or a CertMember-like object.
 */
export function getMemberSurvey(
  cert: Certification,
  certMemberIdOrMember: number | { cert_member_id?: number; id?: number }
): number | null {
  const certMemberId =
    typeof certMemberIdOrMember === 'number'
      ? certMemberIdOrMember
      : certMemberIdOrMember.cert_member_id ??
        (certMemberIdOrMember as { id?: number }).id;
  if (certMemberId == null) {
    return null;
  }
  const member = cert.cert_household?.cert_members.find(
    (m) => m.cert_member_id === certMemberId
  );
  return member?.surveys[0] ?? null;
}

/**
 * Returns true if the given household member has pending interview work:
 * unanswered questions, profile gaps, or signatures to collect. These are the
 * actions the interview can actually resolve, so resuming it will show work.
 *
 * Document actions (rejected/insufficient files) are intentionally excluded —
 * resuming the interview does not surface them, so treating them as interview
 * work made "Continue interview" land on an already-complete questionnaire
 * (ENG-3187). Use `memberNeedsDocumentsFromStatus` for those.
 */
export function memberInterviewNeedsActionFromStatus(
  status: CertStatusDetail | null | undefined,
  userId: number | null | undefined,
  certMemberId: number | null | undefined
): boolean {
  return (
    memberNeedsQuestionsFromStatus(status, certMemberId) ||
    memberHasProfileGapsFromStatus(status, certMemberId) ||
    memberNeedsSignaturesFromStatus(status, userId)
  );
}

/** Returns true if the member has unanswered required questions. */
export function memberNeedsQuestionsFromStatus(
  status: CertStatusDetail | null | undefined,
  certMemberId: number | null | undefined
): boolean {
  if (certMemberId == null) {
    return false;
  }
  const count =
    status?.action_items?.missing_questions?.by_cert_member?.[
      String(certMemberId)
    ]?.count ?? 0;
  return count > 0;
}

/** Returns true if the member's profile has required gaps to fill in. */
export function memberHasProfileGapsFromStatus(
  status: CertStatusDetail | null | undefined,
  certMemberId: number | null | undefined
): boolean {
  if (certMemberId == null) {
    return false;
  }
  return (
    status?.action_items?.profile_gaps_cert_member_ids?.includes(
      certMemberId
    ) ?? false
  );
}

/** Returns true if the member has signatures still to collect. */
export function memberNeedsSignaturesFromStatus(
  status: CertStatusDetail | null | undefined,
  userId: number | null | undefined
): boolean {
  if (userId == null) {
    return false;
  }
  const count =
    status?.action_items?.pending_signatures?.by_recipient?.[String(userId)]
      ?.count ?? 0;
  return count > 0;
}

/**
 * Returns true if the member has document work: a rejected document or a file
 * question with fewer uploads than required.
 */
export function memberNeedsDocumentsFromStatus(
  status: CertStatusDetail | null | undefined,
  certMemberId: number | null | undefined
): boolean {
  if (certMemberId == null) {
    return false;
  }
  const ai = status?.action_items;
  return Boolean(
    ai?.rejected_docs_cert_member_ids?.includes(certMemberId) ||
      ai?.insufficient_files_cert_member_ids?.includes(certMemberId)
  );
}

/**
 * Returns true if the given household member has pending actions according to
 * the cert-status endpoint's per-member `action_items` breakdown.
 */
export function memberNeedsActionFromStatus(
  status: CertStatusDetail | null | undefined,
  userId: number | null | undefined,
  certMemberId: number | null | undefined
): boolean {
  return (
    memberInterviewNeedsActionFromStatus(status, userId, certMemberId) ||
    memberNeedsDocumentsFromStatus(status, certMemberId)
  );
}

export type CertStatusState = 'decision' | 'action' | 'review';

/**
 * Phase-aware state for the cert status chip (ENG-3451).
 *
 * Thor assigns each cert a lifecycle phase. Terminal and adverse phases mean a
 * decision has been issued and no renter action is expected — those show a
 * "decision" state rather than "needs action" or "in review".
 */
export function certStatusState(
  status: CertStatusDetail | null | undefined,
  userId: number | null | undefined,
  certMemberId: number | null | undefined
): CertStatusState {
  if (status?.phase && DECISION_PHASES.includes(status.phase)) {
    return 'decision';
  }
  return memberNeedsActionFromStatus(status, userId, certMemberId)
    ? 'action'
    : 'review';
}
